import { ICoreContext } from '@msdyn365-commerce/core';
import { CommerceProperty, Customer } from '@msdyn365-commerce/retail-proxy';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import jwtDecode from 'jwt-decode';

export interface INewsletterChoiceClaim {
    name: string;
    yesValue: string;
}

// Not locale-dependent, so hardcoded rather than CMS-configured.
export const NEWSLETTER_ATTRIBUTE_NAME = 'BS_ALLOWRECEIVENEWSLETTER';
export const NEWSLETTER_CHOICE_CLAIM: INewsletterChoiceClaim = {
    name: 'extension_newsletterConsentChoice',
    yesValue: 'SubscribeToNewsletterYes'
};

export interface ISynchronizeNewsletterConsentOptions {
    context: ICoreContext;
    customer: Customer;
    token: string;
    attributeName: string;
    choiceClaim: INewsletterChoiceClaim;
}

export type NewsletterConsentEligibility = 'eligible' | 'invalid-token' | 'missing-claim' | 'attribute-missing' | 'already-subscribed';

const decodeToken = (token: string): Record<string, unknown> | undefined => {
    try {
        return jwtDecode<Record<string, unknown>>(token);
    } catch {
        return undefined;
    }
};

const findNewsletterAttribute = (customer: Customer, attributeName: string): CommerceProperty | undefined =>
    customer.ExtensionProperties?.find(property => property.Key === attributeName);

/**
 * Determines whether the customer is eligible for one-way newsletter consent
 * synchronization. A customer is eligible only when the token carries the
 * affirmative choice claim and the D365 attribute exists with value 0.
 * A missing attribute is ineligible; consent is never inferred.
 */
export const getNewsletterConsentEligibility = (
    options: Omit<ISynchronizeNewsletterConsentOptions, 'context'>
): NewsletterConsentEligibility => {
    const { customer, token, attributeName, choiceClaim } = options;

    const claims = decodeToken(token);
    if (!claims) {
        return 'invalid-token';
    }

    if (claims[choiceClaim.name] !== choiceClaim.yesValue) {
        return 'missing-claim';
    }

    const attribute = findNewsletterAttribute(customer, attributeName);
    if (!attribute) {
        return 'attribute-missing';
    }

    if (attribute.Value?.IntegerValue !== 0) {
        return 'already-subscribed';
    }

    return 'eligible';
};

/**
 * Persists the affirmative newsletter choice to D365 by updating the customer
 * extension attribute to IntegerValue 1. The supplied customer object is not
 * mutated; the update is performed on a copy. The attribute is never created
 * when missing.
 */
export const synchronizeNewsletterConsent = async (options: ISynchronizeNewsletterConsentOptions): Promise<void> => {
    const { context, customer, attributeName } = options;

    const updatedCustomer: Customer = {
        ...customer,
        ExtensionProperties: (customer.ExtensionProperties ?? []).map(property =>
            property.Key === attributeName ? { ...property, Value: { ...property.Value, IntegerValue: 1 } } : property
        )
    };

    await updateAsync({ callerContext: context.actionContext }, updatedCustomer);
};
